import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Evento } from 'models/evento_model';
import {
  getEventos,
  createEvento,
  createAllEventos,
  editEvento,
  deleteEvento,
} from 'repositories/evento_repository';

interface EventoState {
  eventos: Evento[];
  evento: Evento | null;
  loading: boolean;
  error: string | null;
}

const initialState: EventoState = {
  eventos: [],
  evento: null,
  loading: false,
  error: null,
};

export const cargarEventos = createAsyncThunk<Evento[], number[], { rejectValue: string }>(
  'evento/cargarEventos',
  async (filterYears, { rejectWithValue }) => {
    try {
      return ((await getEventos(filterYears)) ?? []) as Evento[];
    } catch {
      return rejectWithValue('Error al cargar eventos');
    }
  },
);

export const agregarEvento = createAsyncThunk(
  'evento/agregarEvento',
  async (data: Evento, { rejectWithValue }) => {
    try {
      await createEvento(data);
    } catch {
      return rejectWithValue('Error al crear evento');
    }
  },
);

export const agregarTodosEventos = createAsyncThunk(
  'evento/agregarTodosEventos',
  async (data: Evento[], { rejectWithValue }) => {
    try {
      await createAllEventos(data);
    } catch {
      return rejectWithValue('Error al crear eventos');
    }
  },
);

export const editarEvento = createAsyncThunk(
  'evento/editarEvento',
  async ({ data, eventoId }: { data: Evento; eventoId: string }, { rejectWithValue }) => {
    try {
      await editEvento(data, eventoId);
    } catch {
      return rejectWithValue('Error al editar evento');
    }
  },
);

export const eliminarEvento = createAsyncThunk(
  'evento/eliminarEvento',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteEvento(id);
    } catch {
      return rejectWithValue('Error al eliminar evento');
    }
  },
);

export const eventoSlice = createSlice({
  name: 'evento',
  initialState,
  reducers: {
    changeEvento: (state, action: PayloadAction<Evento | null>) => {
      state.evento = action.payload;
    },
    setEventos: (state, action: PayloadAction<Evento[]>) => {
      state.eventos = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarEventos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarEventos.fulfilled, (state, action) => {
        state.loading = false;
        state.eventos = action.payload;
      })
      .addCase(cargarEventos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { changeEvento, setEventos } = eventoSlice.actions;
export default eventoSlice.reducer;
